import React from "react";
import { Routes, Route, createRoutesFromElements, matchRoutes } from "react-router-dom";
import createRoutePage from "./routing/createRoutePage";
import Scroll from "react-scroll";
import ScrollToTop from "./ScrollToTop";
import RouteSeo from "./seo/RouteSeo";
import { COMPARISONS, planPath } from "./plans/planPagesData";
import ratesData from "./data/ratesData";
import { bootLab } from "./designLab/labOptions";

const AgentResourcesPage = createRoutePage(() => import("./AgentResourcesPage"));
const IncomePercentilePage = createRoutePage(() => import("./calculators/incomePercentile/IncomePercentilePage"));
const DecisionGuidePage = createRoutePage(() => import("./guides/DecisionGuidePage"));
const DecisionToolPage = createRoutePage(() => import("./calculators/decisionTools/DecisionToolPage"));
const AppPage = createRoutePage(() => import("./AppPage"));
const AboutPage = createRoutePage(() => import("./AboutPage"));
const CalculatorsPage = createRoutePage(() => import("./CalculatorsPage"));
const GuidesPage = createRoutePage(() => import("./GuidesPage"));
const FaqPage = createRoutePage(() => import("./FaqPage"));
const PrivacyPolicyPage = createRoutePage(() => import("./PrivacyPolicyPage"));
const MortgagePage = createRoutePage(() => import("./calculators/mortgageRepayments/pages/MortgageAppPage"));
const OverpaymentPage = createRoutePage(() => import("./calculators/mortgageOverpayments/pages/OverpaymentAppPage"));
const InvestmentPage = createRoutePage(() => import("./calculators/next/pages/InvestmentAppPage"));
const FirePage = createRoutePage(() => import("./calculators/fire/pages/FireAppPage"));
const StampDutyPage = createRoutePage(() => import("./calculators/stampDuty/pages/StampDutyAppPage"));
const IncomeTaxPage = createRoutePage(() => import("./calculators/incomeTax/pages/IncomeTaxAppPage"));
const TakeHomePayPage = createRoutePage(() => import("./calculators/takeHomePay/pages/TakeHomePayAppPage"));
const MarginalRatePage = createRoutePage(() => import("./calculators/marginalRate/pages/MarginalRateAppPage"));
const PensionPage = createRoutePage(() => import("./calculators/pension/pages/PensionAppPage"));
const SavingsGoalPage = createRoutePage(() => import("./calculators/savingsGoal/pages/SavingsGoalAppPage"));
const FirstHomePage = createRoutePage(() => import("./calculators/firstHome/pages/FirstHomeAppPage"));
const MoneyPlannerPage = createRoutePage(() => import("./calculators/moneyPlanner/pages/MoneyPlannerAppPage"));
const CarLoanPage = createRoutePage(() => import("./calculators/carLoan/pages/CarLoanAppPage"));
const CreditCardPage = createRoutePage(() => import("./calculators/creditCard/pages/CreditCardAppPage"));
const DebtPayoffPage = createRoutePage(() => import("./calculators/debtPayoff/pages/DebtPayoffAppPage"));
const RemortgagePage = createRoutePage(() => import("./calculators/remortgage/pages/RemortgageAppPage"));
const BorrowPage = createRoutePage(() => import("./calculators/borrow/pages/BorrowAppPage"));
const StudentBudgetPage = createRoutePage(() => import("./calculators/studentBudget/pages/StudentBudgetAppPage"));
const RatesPage = createRoutePage(() => import("./RatesPage"));
const OverpayGuidePage = createRoutePage(() => import("./guides/OverpayGuidePage"));
const CheckBalanceGuidePage = createRoutePage(() => import("./guides/CheckBalanceGuidePage"));
const InterestGuidePage = createRoutePage(() => import("./guides/InterestGuidePage"));
const WriteOffGuidePage = createRoutePage(() => import("./guides/WriteOffGuidePage"));
const RealVsNominalGuidePage = createRoutePage(() => import("./guides/RealVsNominalGuidePage"));
const PayslipMechanicsGuidePage = createRoutePage(() => import("./guides/PayslipMechanicsGuidePage"));
const CompoundInterestGuidePage = createRoutePage(() => import("./guides/CompoundInterestGuidePage"));
const SixtyPercentTrapGuidePage = createRoutePage(() => import("./guides/SixtyPercentTrapGuidePage"));
const SalarySacrificeGuidePage = createRoutePage(() => import("./guides/SalarySacrificeGuidePage"));
const LisaRulesGuidePage = createRoutePage(() => import("./guides/LisaRulesGuidePage"));
const OverpayOrInvestGuidePage = createRoutePage(() => import("./guides/OverpayOrInvestGuidePage"));
const DesignLabPage = createRoutePage(() => import("./designLab/DesignLabPage"));
const PlanFinderPage = createRoutePage(() => import("./PlanFinderPage"));
const MaintenanceLoanPage = createRoutePage(() => import("./MaintenanceLoanPage"));
const PlanPage = createRoutePage(() => import("./plans/PlanPage"));
const PlanComparePage = createRoutePage(() => import("./plans/PlanComparePage"));
const NotFoundPage = createRoutePage(() => import("./NotFoundPage"));
const EquityPage = createRoutePage(() => import("./calculators/equity/EquityPage"));
const JobOffersPage = createRoutePage(() => import("./calculators/jobOffers/JobOffersPage"));

// Design lab choices saved in this browser apply to every page.
bootLab();

const Element = Scroll.Element;

// The router and publication verifier consume the same actual route elements.
export const getRouteElements = () => (
  <>

        <Route exact path="/" element={<AppPage />} />
        <Route exact path="/mortgage" element={<MortgagePage />} />
        <Route
          exact
          path="/mortgage/overpayments"
          element={<OverpaymentPage />}
        />
        <Route exact path="/investment" element={<InvestmentPage />} />
        <Route exact path="/fire" element={<FirePage />} />
        <Route exact path="/stamp-duty" element={<StampDutyPage />} />
        <Route path="/required-salary" element={<DecisionToolPage key="requiredSalary" type="requiredSalary" />} />
        <Route path="/pro-rata-pay" element={<DecisionToolPage key="proRataPay" type="proRataPay" />} />
        <Route path="/inflation" element={<DecisionToolPage key="inflation" type="inflation" />} />
        <Route path="/rent-vs-buy" element={<DecisionToolPage key="rentVsBuy" type="rentVsBuy" />} />
        <Route path="/for-agents" element={<AgentResourcesPage />} />
        <Route path="/income-percentile" element={<IncomePercentilePage />} />
        <Route path="/guides/car-finance-total-cost" element={<DecisionGuidePage key="car" type="car" />} />
        <Route path="/guides/compare-job-offers" element={<DecisionGuidePage key="offers" type="offers" />} />
        <Route path="/guides/rent-or-buy-assumptions" element={<DecisionGuidePage key="home" type="home" />} />
        <Route exact path="/income-tax" element={<IncomeTaxPage />} />
        <Route exact path="/take-home-pay" element={<TakeHomePayPage />} />
        <Route exact path="/marginal-rate" element={<MarginalRatePage />} />
        <Route exact path="/pension" element={<PensionPage />} />
        <Route exact path="/savings-goal" element={<SavingsGoalPage />} />
        <Route exact path="/first-home" element={<FirstHomePage />} />
        <Route exact path="/money-planner" element={<MoneyPlannerPage />} />
        <Route exact path="/car-loan" element={<CarLoanPage />} />
        <Route exact path="/credit-card" element={<CreditCardPage />} />
        <Route exact path="/debt-payoff" element={<DebtPayoffPage />} />
        <Route exact path="/remortgage" element={<RemortgagePage />} />
        <Route exact path="/how-much-can-i-borrow" element={<BorrowPage />} />
        <Route exact path="/student-budget" element={<StudentBudgetPage />} />
        <Route exact path="/rsu-calculator" element={<EquityPage kind="rsu" />} />
        <Route exact path="/share-options" element={<EquityPage kind="options" />} />
        <Route exact path="/share-incentive-plan" element={<EquityPage kind="sip" />} />
        <Route exact path="/compare-job-offers" element={<JobOffersPage />} />
        <Route path="/calculators" element={<CalculatorsPage />} />
        <Route path="/guides" element={<GuidesPage />} />
        <Route path="/design-lab" element={<DesignLabPage />} />
        <Route path="/which-plan" element={<PlanFinderPage />} />
        <Route path="/maintenance-loan" element={<MaintenanceLoanPage />} />
        {ratesData.plans.map((plan) => (
          <Route
            key={plan.id}
            path={planPath(plan.id)}
            element={<PlanPage planId={plan.id} />}
          />
        ))}
        {COMPARISONS.map((c) => (
          <Route
            key={c.slug}
            path={`/${c.slug}`}
            element={<PlanComparePage slug={c.slug} />}
          />
        ))}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/faqs" element={<FaqPage />} />
        <Route path="/rates" element={<RatesPage />} />
        <Route path="/guides/should-i-overpay" element={<OverpayGuidePage />} />
        <Route path="/guides/check-student-loan-balance" element={<CheckBalanceGuidePage />} />
        <Route
          path="/guides/how-interest-works"
          element={<InterestGuidePage />}
        />
        <Route
          path="/guides/student-loan-write-off"
          element={<WriteOffGuidePage />}
        />
        <Route
          path="/guides/real-vs-nominal-money"
          element={<RealVsNominalGuidePage />}
        />
        <Route
          path="/guides/how-repayments-leave-your-payslip"
          element={<PayslipMechanicsGuidePage />}
        />
        <Route
          path="/guides/compound-interest-maths"
          element={<CompoundInterestGuidePage />}
        />
        <Route
          path="/guides/60-percent-tax-trap"
          element={<SixtyPercentTrapGuidePage />}
        />
        <Route
          path="/guides/salary-sacrifice-and-student-loans"
          element={<SalarySacrificeGuidePage />}
        />
        <Route
          path="/guides/lifetime-isa-rules"
          element={<LisaRulesGuidePage />}
        />
        <Route
          path="/guides/overpay-or-invest"
          element={<OverpayOrInvestGuidePage />}
        />
        <Route path="/privacypolicy" element={<PrivacyPolicyPage />} />
        <Route path="*" element={<NotFoundPage />} />

  </>
);

// Use the real router inventory, including the 404 and shared plan pages.
export function preloadRoute(pathname) {
  const matches = matchRoutes(createRoutesFromElements(getRouteElements()), pathname) || [];
  return Promise.all(matches.map(({ route }) => route.element.type.preload()));
}

export function preloadAllRoutes() {
  const pages = new Set(createRoutesFromElements(getRouteElements()).map(({ element }) => element.type));
  return Promise.all([...pages].map((page) => page.preload()));
}

function AppRoutes({ includeScrollArtifacts = true }) {
  return (
    <>
      {includeScrollArtifacts ? <ScrollToTop /> : null}
      <RouteSeo />
      {includeScrollArtifacts ? <Element name="topScrollToEvent" /> : null}
      <Routes>{getRouteElements()}</Routes>
    </>
  );
}

export default AppRoutes;
